/**
 * Patterns matching third-party browser-extension script URLs that leak into
 * Sentry via the global unhandledrejection / onerror handlers.
 *
 * MOVEPRO-BOOK-AH: PayPal Honey Safari extension throws `UnavailableError`
 * while the customer is on Book Review — stack is entirely Honey.safariextension.
 *
 * Keep every pattern extension-specific. Do NOT add `webkit-masked-url://hidden/`:
 * WebKit uses it for any cross-origin script frame without CORS attribution, not
 * just extensions, so it would also match Stripe / Maps / PostHog frames on
 * Review + Checkout. Because `shouldDropBrowserExtensionSentryEvent` matches on
 * *any* frame in the stack, a hybrid Book+masked stack would be dropped silently
 * — a real checkout error going dark with no signal that it happened. It also
 * buys nothing: BOOK-AH's frames are plain file paths, and no event in the last
 * 90 days across the org carried a masked frame.
 */
export const BROWSER_EXTENSION_URL_PATTERNS: RegExp[] = [
    /Honey\.safariextension/i,
    /PayPal[\s%20._-]*Honey/i,
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /safari-web-extension:\/\//i,
    /safari-extension:\/\//i,
];

type SentryFrame = { filename?: string | null; abs_path?: string | null };

type SentryExceptionValue = {
    type?: string | null;
    value?: string | null;
    stacktrace?: { frames?: SentryFrame[] | null } | null;
};

type SentryEventLike = {
    exception?: { values?: SentryExceptionValue[] | null } | null;
};

function frameUrl(frame: SentryFrame): string {
    return frame.filename || frame.abs_path || '';
}

function matchesBrowserExtensionUrl(url: string): boolean {
    return BROWSER_EXTENSION_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Whether a Sentry event should be dropped because it originated from a
 * browser extension (e.g. PayPal Honey), not first-party Book code.
 */
export function shouldDropBrowserExtensionSentryEvent(
    event: SentryEventLike | undefined,
    hint?: { originalException?: unknown },
): boolean {
    const frames = event?.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

    if (frames.some((frame) => matchesBrowserExtensionUrl(frameUrl(frame)))) {
        return true;
    }

    const original = hint?.originalException;
    if (original && typeof original === 'object') {
        const stack = 'stack' in original && typeof original.stack === 'string' ? original.stack : '';
        if (stack && matchesBrowserExtensionUrl(stack)) {
            return true;
        }
    }

    return false;
}
